// src/components/OcrImageUploader.js

import React, { useState, useRef } from "react";
// Import the same CSS that defines .image-upload-container, .image-upload-label, etc.
import "../pages/ViewMap/style.css";

import { createWorker } from "tesseract.js";

/**
 * Props:
 *   onParsed: ({ distanceKm, durationSec }) => void
 *   onError: (errorMessage: string) => void   (optional)
 *
 * Behavior:
 *   • Allows click or drag‐drop of one or more images.
 *   • Shows a CSS spinner while Tesseract processes them.
 *   • Parses “distance” and “time” (HH:MM:SS, MM:SS, bare MMSS, Xm Ys, “X min, Y sec(s)”, etc.).
 *   • Sums distances/durations across all files and calls onParsed({ distanceKm, durationSec }).
 */

const worker = createWorker({
  logger: (m) => {
    // optional: console.log(m.status, m.progress);
  },
});

export default function OcrImageUploader({ onParsed, onError }) {
  // status: "idle" | "processing" | "done" | "nodata" | "error"
  const [status, setStatus] = useState("idle");
  const [fileCount, setFileCount] = useState(0);
  const fileInputRef = useRef(null);

  // Called when user selects files (via hidden input)
  const handleFileSelection = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) {
      alert("Please select one or more image files.");
      return;
    }

    setFileCount(files.length);
    setStatus("processing");

    let totalDistance = 0;
    let totalDuration = 0;
    let foundAnyData = false;

    try {
      // 1) Initialize Tesseract
      await worker.load();
      await worker.loadLanguage("eng");
      await worker.initialize("eng");

      // 2) Recognize each file in sequence
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const {
          data: { text },
        } = await worker.recognize(file);
        console.log("🖥️ OCR returned raw text for one image:", text);

        const { distanceKm, durationSec } = parseDistanceAndTime(text);

        if (distanceKm != null) {
          totalDistance += distanceKm;
          foundAnyData = true;
        }
        if (durationSec != null) {
          totalDuration += durationSec;
          foundAnyData = true;
        }
      }

      // 3) Check result
      if (!foundAnyData) {
        setStatus("nodata");
        onError &&
          onError(
            "No valid distance or time was found in any of the selected images."
          );
      } else {
        setStatus("done");
        onParsed({ distanceKm: totalDistance, durationSec: totalDuration });
      }
    } catch (err) {
      console.error("Tesseract error:", err);
      setStatus("error");
      onError &&
        onError(
          "OCR failed on one or more images. Please try again with clearer photos."
        );
    }
  };

  // Hidden input’s onChange
  const handleInputChange = (e) => handleFileSelection(e.target.files);

  // Drag‐and‐drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelection(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  /**
   * Parses raw OCR text for:
   *   • distanceKm (number|null)
   *   • durationSec (number|null)
   *
   * Algorithm:
   *   1) Remove any “time-of-day” like “HH:MM AM/PM”.
   *   2) Remove any pace‐style substrings like “MM:SS /km”, “MM:SS/km Avg Pace”, “MM:SS /mi Avg Speed”.
   *   3) Collapse all whitespace into single spaces.
   *   4) Parse distance exactly as before.
   *   5) If “sleep” is present, return null time immediately.
   *   6) Collect every “label” (“Total Time”, “Moving Time”, “Duration”, “Time”) and record its string index.
   *   7) Collect every “time‐like” token (`HH:MM:SS`, `MM:SS`), then Fitbit’s “X min, Y sec(s)”, then “Xm…Ys” fallback.
   *   8) For each time‐token, compute the minimum absolute difference to any label‐index.
   *      Pick the time whose distance to its nearest label is the smallest—provided that distance < 60.
   *   9) Convert that matched time‐string to seconds.
   *  10) Return { distanceKm, durationSec }.
   *
   * This ensures that:
   *   • “44:17 307 Total Time” → picks “44:17” (distance ~7 chars away).
   *   • “14:45 /km Avg Pace” (or “09:51 AM”) never makes it this far.
   *   • “Duration 1min, 55 secs” (Fitbit style) also gets picked up.
   */
  function parseDistanceAndTime(rawText) {
    // ─────────────────────────────────────────────────────────────────────────
    // 1) Log raw OCR output for debugging:
    console.log("OCR raw text:", JSON.stringify(rawText));
    // ─────────────────────────────────────────────────────────────────────────

    // STEP 1) Remove any “time‐of‐day” like “12:05 AM” or “9:51PM”
    let cleaned = rawText.replace(
      /\b(\d{1,2}):(\d{2})\s*(?:AM|PM)\b/gi,
      ""
    );

    // STEP 2) Remove any pace substrings: “MM:SS /km”, “MM:SS/km Avg Pace”, “MM:SS /mi Avg Speed”
    cleaned = cleaned.replace(
      /\b(\d{1,2}):(\d{2})\s*\/\s*(?:km|mi|miles?)\s*(?:Avg\s*(?:Pace|Speed))?\b/gi,
      ""
    );
    // Also drop any stray “Avg Pace” or “Avg Speed” leftover after OCR splits
    cleaned = cleaned.replace(/\bAvg\s*(?:Pace|Speed)\b/gi, "");

    // STEP 3) Collapse ALL whitespace into single spaces, then trim
    // e.g. “Total Time\n44:17” → “Total Time 44:17”
    const text = cleaned.replace(/\s+/g, " ").trim();

    let distanceKm = null;
    let durationSec = null;

    // ─── DISTANCE PARSING (unchanged) ───────────────────────────────────────
    // a) “<float> Distance (mi)” or “<float> Distance (km)”
    let dm = text.match(/([\d]+\.\d+)\s*Distance\s*\(\s*(mi|km)\s*\)/i);
    if (dm) {
      const rawValue = parseFloat(dm[1]);
      const unit = dm[2].toLowerCase();
      distanceKm =
        unit === "mi"
          ? parseFloat((rawValue * 1.60934).toFixed(2))
          : rawValue;
    } else {
      // a2) “Distance (mi) <float>” or “Distance (km) <float>”
      dm = text.match(/Distance\s*\(\s*(mi|km)\s*\)\s*([\d]+\.\d+)/i);
      if (dm) {
        const rawValue = parseFloat(dm[2]);
        const unit = dm[1].toLowerCase();
        distanceKm =
          unit === "mi"
            ? parseFloat((rawValue * 1.60934).toFixed(2))
            : rawValue;
      } else {
        // b) “<float> mi” or “<float> miles”
        dm =
          text.match(/([\d]+\.\d+).*?mi\b/i) ||
          text.match(/([\d]+\.\d+).*?miles?\b/i);
        if (dm) {
          const miles = parseFloat(dm[1]);
          distanceKm = parseFloat((miles * 1.60934).toFixed(2));
        } else {
          // c) “<float> km” or “<float> k”
          dm =
            text.match(/([\d]+\.\d+).*?km\b/i) ||
            text.match(/([\d]+\.\d+).*?k\b/i);
          if (dm) {
            distanceKm = parseFloat(dm[1]);
          }
        }
      }
    }

    // STEP 4) If “sleep” appears anywhere, bail out on time
    if (/sleep/i.test(rawText)) {
      return { distanceKm, durationSec: null };
    }

    // ─── TIME PARSING (nearest‐label + Fitbit fallback) ───────────────────────
    // STEP 5) Collect all label‐like matches (“Total Time”, “Moving Time”, “Duration”, “Time”)
    const lower = text.toLowerCase();
    const labelRegex = /\b(?:total\s*time|moving\s*time|duration|time)\b/gi;
    const labelIndexes = [];
    let lm;
    while ((lm = labelRegex.exec(lower)) !== null) {
      labelIndexes.push(lm.index);
    }
    // If we never found ANY label, give up on time:
    if (labelIndexes.length === 0) {
      return { distanceKm, durationSec: null };
    }

    // STEP 6) Collect all “time‐like” candidates:
    //   A) HH:MM:SS or MM:SS
    //   B) Fitbit‐style “X min, Y sec(s)”
    //   C) Old fallback “Xm…Ys”

    // 6A) HH:MM:SS or MM:SS
    const timeRegex = /\b\d{1,2}:\d{2}(?::\d{2})?\b/g;
    const timeCandidates = []; // will hold { text, idx, isMinSecCombo?, minutes?, seconds? }
    let tm;
    while ((tm = timeRegex.exec(text)) !== null) {
      timeCandidates.push({
        text: tm[0],       // e.g. “44:17” or “01:47:41”
        idx: tm.index,
        isMinSecCombo: false,
      });
    }

    // 6B) Fitbit‐style “X min, Y sec(s)” (e.g. “1 min, 55 secs”, “1min, 55 secs”)
    const fitbitRegex =
      /(\d{1,3})\s*min\s*,\s*(\d{1,2})\s*sec(?:s)?\b/gi;
    let fbm;
    while ((fbm = fitbitRegex.exec(text)) !== null) {
      timeCandidates.push({
        text: `${fbm[1]}m${fbm[2]}s`, // normalize to “XmYs”
        idx: fbm.index,
        isMinSecCombo: true,
        minutes: parseInt(fbm[1], 10),
        seconds: parseInt(fbm[2], 10),
      });
    }

    // 6C) Old fallback “Xm…Ys” (e.g. “42m 2s”, “42min2sec”)
    const oldFallbackRegex =
      /(\d{1,3})m(?:in(?:ute)?s?)?(\d{1,2})s(?:ec(?:ond)?s?)?/gi;
    let ofm;
    while ((ofm = oldFallbackRegex.exec(text)) !== null) {
      timeCandidates.push({
        text: `${ofm[1]}m${ofm[2]}s`,
        idx: ofm.index,
        isMinSecCombo: true,
        minutes: parseInt(ofm[1], 10),
        seconds: parseInt(ofm[2], 10),
      });
    }

    // If no time candidates at all, give up:
    if (timeCandidates.length === 0) {
      return { distanceKm, durationSec: null };
    }

    // STEP 7) For each timeCandidate, find the minimum absolute distance to any label index:
    let bestMatch = null; // { text, idx, distance, isMinSecCombo?, minutes?, seconds? }
    for (let t of timeCandidates) {
      let minDist = Infinity;
      for (let lblIdx of labelIndexes) {
        const d = Math.abs(t.idx - lblIdx);
        if (d < minDist) {
          minDist = d;
        }
      }
      if (bestMatch === null || minDist < bestMatch.distance) {
        bestMatch = { ...t, distance: minDist };
      }
    }

    // STEP 8) Only accept it if that closest‐distance < 60 characters
    if (bestMatch.distance < 60) {
      // Convert to seconds:
      if (bestMatch.isMinSecCombo) {
        durationSec = bestMatch.minutes * 60 + bestMatch.seconds;
      } else {
        const parts = bestMatch.text.split(":").map((x) => parseInt(x, 10));
        if (parts.length === 3) {
          durationSec = parts[0] * 3600 + parts[1] * 60 + parts[2];
        } else {
          durationSec = parts[0] * 60 + parts[1];
        }
      }
    } else {
      // Too far from any label → ignore
      durationSec = null;
    }

    return { distanceKm, durationSec };
  }
  // ─────────────────────────────────────────────────────────────────────────

  // Inline CSS for the spinner (uses @keyframes spin from your CSS)
  const spinnerStyle = {
    margin: "20px auto",
    width: "48px",
    height: "48px",
    border: "5px solid rgba(0, 0, 0, 0.1)",
    borderTop: "5px solid #c17846", // same accent color
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  };

  return (
    <div style={{ marginTop: 10 }}>
      <p>
        <strong>
          Please Upload one or more images to submit your
          distance &amp; time.:
        </strong>
      </p>

      {/* ── Styled drop‐zone: click or drag/drop ────────────────────────── */}
      <div
        className="image-upload-container"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleInputChange}
          style={{ display: "none" }}
        />
        <div className="image-upload-label">
          <div className="upload-icon">📤</div>
          <span className="upload-text">Click or drag &amp; drop to upload</span>
        </div>
      </div>
      {/* ──────────────────────────────────────────────────────────────── */}

      {status === "processing" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <div style={spinnerStyle} />
          <p style={{ marginTop: 10 }}>
            Processing {fileCount} image{fileCount > 1 ? "s" : ""}… please
            wait.
          </p>
        </div>
      )}
      {status === "done" && (
        <p style={{ color: "green", marginTop: 10 }}>
          All images processed successfully. You may proceed with the combined
          result.
        </p>
      )}
      {status === "nodata" && (
        <p style={{ color: "orange", marginTop: 10 }}>
          No valid distance or time was found in any of the selected images.
        </p>
      )}
      {status === "error" && (
        <p style={{ color: "red", marginTop: 10 }}>
          An error occurred during OCR. Please try again with clearer images.
        </p>
      )}
    </div>
  );
}
